// ServicesPage.jsx — CONTAINER (dono da lógica). Monta { vm, actions } reais e renderiza a
// ServicesView (View pura do Claude Design). Formato View/ViewModel à risca (docs/DESIGN-FLOW.md).
// O visual é da View; aqui só há lógica/dados/dialogs. Backup do legado: ServicesPage.legacy.jsx.bak.
import { useState, useMemo, useCallback } from 'react';

import { useServices } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { formatCurrency, formatDuration } from '../utils/formatters';
import photoService from '../services/photoService';

import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import AppSnackbar from '../components/ui/AppSnackbar';
import ServiceForm from '../components/forms/ServiceForm';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import ServicesView from './services/ServicesView';

function getServiceInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nome A → Z' },
  { value: 'name_desc', label: 'Nome Z → A' },
  { value: 'duration_asc', label: 'Menor duração' },
  { value: 'price_asc', label: 'Menor preço' },
];

export default function ServicesPage() {
  const { items: services, loading, error, createAsync, updateAsync, reload } = useServices();
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const photoDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const [photoTimestampOverrides, setPhotoTimestampOverrides] = useState({});
  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState('name_asc');

  const filteredServices = useMemo(() => {
    let result = [...(services || [])];
    const q = searchText.trim().toLowerCase();
    if (q) {
      result = result.filter(
        s =>
          (s.name || '').toLowerCase().includes(q) ||
          (s.displayName || '').toLowerCase().includes(q) ||
          (s.description || '').toLowerCase().includes(q) ||
          (s.category || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      if (sortOrder === 'name_desc') return (b.name || '').localeCompare(a.name || '', 'pt-BR');
      if (sortOrder === 'duration_asc')
        return (a.defaultDurationMinutes || 0) - (b.defaultDurationMinutes || 0);
      if (sortOrder === 'price_asc') return (a.defaultPrice || 0) - (b.defaultPrice || 0);
      return (a.name || '').localeCompare(b.name || '', 'pt-BR');
    });
    return result;
  }, [services, searchText, sortOrder]);

  const isFiltered = searchText.trim() !== '';
  const canClear = searchText !== '' || sortOrder !== 'name_asc';
  const viewState = loading
    ? 'loading'
    : error
      ? 'error'
      : (services?.length ?? 0) === 0
        ? 'empty'
        : 'ready';

  const items = useMemo(
    () =>
      filteredServices.map(s => {
        void photoTimestampOverrides[s.id];
        const hasDuration = s.defaultDurationMinutes > 0;
        const hasPrice = s.defaultPrice > 0;
        return {
          id: s.id,
          name: s.name,
          displayName: s.displayName && s.displayName !== s.name ? s.displayName : null,
          description: s.description || null,
          initials: getServiceInitials(s.name),
          photoUrl: photoService.getServicePhotoUrl(s.userId, s.id),
          durationLabel: hasDuration ? formatDuration(s.defaultDurationMinutes) : null,
          priceLabel: hasPrice ? formatCurrency(s.defaultPrice) : null,
          category: s.category || null,
        };
      }),
    [filteredServices, photoTimestampOverrides]
  );

  const vm = {
    viewState,
    errorMessage: error ? 'Não foi possível carregar os serviços. Tente novamente.' : null,
    title: 'Serviços',
    subtitle: 'Catálogo de serviços oferecidos',
    actionLabel: 'Novo Serviço',
    filters: { searchText, sortOrder, sortOptions: SORT_OPTIONS, isFiltered, canClear },
    items,
    emptyFilteredMessage: isFiltered
      ? 'Nenhum serviço encontrado para "' + searchText.trim() + '"'
      : 'Nenhum serviço cadastrado',
  };

  // Erro do servidor que aponta um campo (`{ message, field }`). O diálogo continua aberto,
  // então a mensagem fica NO campo — não só num toast que some antes de a pessoa ler.
  const [serverError, setServerError] = useState(null);

  const handleCreate = async data => {
    setServerError(null);
    try {
      await createAsync(data);
      formDialog.handleClose();
      showSuccess('Serviço criado com sucesso!');
    } catch (err) {
      const body = err.response?.data;
      if (body?.field) setServerError({ field: body.field, message: body.message });
      showError('Erro ao criar: ' + (body?.message || err.message));
    }
  };

  const handleUpdate = async data => {
    setServerError(null);
    try {
      await updateAsync(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Serviço atualizado com sucesso!');
    } catch (err) {
      const body = err.response?.data;
      if (body?.field) setServerError({ field: body.field, message: body.message });
      showError('Erro ao atualizar: ' + (body?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handlePhotoUpload = async file => {
    const service = photoDialog.data;
    if (!service?.id) {
      showError('Serviço não selecionado');
      return;
    }
    try {
      await photoService.uploadServicePhoto(file, service.id);
      const timestamp = Date.now();
      photoService.saveServicePhotoTimestamp(service.id, timestamp);
      setPhotoTimestampOverrides(prev => ({ ...prev, [service.id]: timestamp }));
      photoDialog.handleClose();
      showSuccess('Foto atualizada com sucesso!');
      reload();
    } catch (err) {
      showError('Erro ao fazer upload: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteDone = useCallback(() => {
    deleteDialog.handleClose();
    showSuccess('Serviço excluído com sucesso!');
    reload();
  }, [deleteDialog, showSuccess, reload]);

  const byId = useCallback(id => (services || []).find(s => s.id === id), [services]);

  const actions = {
    onNew: () => formDialog.handleOpen(null),
    onEdit: id => formDialog.handleOpen(byId(id)),
    onDelete: id => deleteDialog.handleOpen(byId(id)),
    onPhoto: id => photoDialog.handleOpen(byId(id)),
    onSearchChange: setSearchText,
    onSortChange: setSortOrder,
    onClearFilters: () => { setSearchText(''); setSortOrder('name_asc'); },
    onRetry: reload,
  };

  return (
    <>
      <ServicesView vm={vm} actions={actions} />

      <ServiceForm
        open={formDialog.open}
        onClose={() => { setServerError(null); formDialog.handleClose(); }}
        onSubmit={handleSubmit}
        data={formDialog.data}
        serverError={serverError}
      />

      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={handleDeleteDone}
        deletePath={deleteDialog.data ? '/services/' + deleteDialog.data.id : ''}
        entityName={deleteDialog.data?.name}
      />

      <ProfilePhotoUpload
        open={photoDialog.open}
        onClose={photoDialog.handleClose}
        onUpload={handlePhotoUpload}
        title="Foto do Serviço"
        currentPhotoUrl={
          photoDialog.data
            ? photoService.getServicePhotoUrl(photoDialog.data.userId, photoDialog.data.id)
            : null
        }
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}
